import type { QueryClient } from '@tanstack/react-query';
import { getCookie } from 'cookies-next';

import { appLogger } from '@/utils/app-logger/appLogger';
import { SESSION_ID_COOKIE } from '@/utils/constants/common';
import { type PlatformRestartType, runPlatformRestart } from '@/utils/platformRestart';
import { hasAuthCookie } from '@/utils/session/authCookie';

import { cacheKey as fullUserProfileCacheKey } from '../providers/useFullUser';

type RunPostSignOutClientSetupParams = {
    queryClient: QueryClient;
    platformRestartEnabled: boolean;
    errors?: string[];
};

/**
 * Runs post-logout client steps after logOut succeeds and CBS_COM is cleared.
 * Failures are non-fatal when the auth cookie is absent so nav can refresh to
 * anonymous state (mirrors runPostSignInClientSetup for mobile web hiccups).
 */
export async function runPostSignOutClientSetup({
    queryClient,
    platformRestartEnabled,
    errors = [],
}: RunPostSignOutClientSetupParams): Promise<PlatformRestartType | undefined> {
    let platformRestartResult: PlatformRestartType | undefined;

    if (platformRestartEnabled) {
        try {
            // Authoritative so the post-logout session wins over a concurrent anonymous Start; the
            // anonymous identity clears the no-downgrade marker so a subsequent Start can proceed.
            platformRestartResult = await runPlatformRestart(errors, undefined, {
                authoritative: true,
                identity: 'anonymous',
            });
        } catch (error) {
            if (hasAuthCookie()) {
                throw error;
            }
            appLogger.warn('Sign-out succeeded but platformRestart failed; continuing', {
                component: 'runPostSignOutClientSetup',
                step: 'platformRestart',
            });
        }
        // The restart should have set an anonymous CBS_COM; without it playback fails.
        if (!hasAuthCookie()) {
            appLogger.warn('Sign-out left no auth cookie', {
                component: 'runPostSignOutClientSetup',
                restartSucceeded: platformRestartResult?.platformRestart.success === true,
                hasSessionCookie: Boolean(getCookie(SESSION_ID_COOKIE)),
            });
        }
    }

    try {
        await queryClient.invalidateQueries({ queryKey: [fullUserProfileCacheKey] });
        await queryClient.refetchQueries({ queryKey: [fullUserProfileCacheKey] });
    } catch (error) {
        if (hasAuthCookie()) {
            throw error;
        }
        appLogger.warn('Sign-out succeeded but profile refresh failed; continuing', {
            component: 'runPostSignOutClientSetup',
            step: 'profileRefresh',
        });
    }

    return platformRestartResult;
}
